import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import classNames from 'classnames';
import styles from './RiskDataGrid.module.scss';
import { TableHeader } from '../TableHeader';
import { Loading } from '../../components/Loading';
import { isEmpty, isEmptyArray, numberWithCommas, formatPercent, getQueryString, formatDate, formatSpecialPrice } from '../../util/functions';
import { getData } from '../../api/dashboard';
import { downloadRiskData } from '../../api/riskDashboard';
import DataGrid, { Column, Scrolling, Summary, TotalItem, ColumnFixing, Selection, Export } from 'devextreme-react/data-grid';
import { COLUMN_HEIGHT } from '../../util/dashBoardPageConstants';

const SOCKET_ERROR_MAX_COUNT = 5;
const currentDate = moment();
const RISK_ACK_TEXT_LINK = 'Acknowledge Risk'

const customizeBoolean = (cellInfo) => {
  return cellInfo.value ? 'Yes' : 'No';
};

const customizeDate = (cellInfo) => {
  return  formatDate(cellInfo.value, 'YYYY-MM-DD', 'MM/DD/YYYY')
};

const customizeDateTime = (cellInfo) => {
  const date = cellInfo?.value?.split(" ")[0];
  return  formatDate(date, 'YYYY-MM-DD', 'MM/DD/YYYY')
};

const customizeTime = (cellInfo) => {
  const time = cellInfo?.value?.split(" ")[1];
  return  time;
};

const customizeSide = (cellInfo) => {
  return cellInfo.value ==='B' ? 'Buy' : 'Sell';
};

const customizeSpecialPriceColumn = (data) => {
  if (undefined !== data.value) {
    return formatSpecialPrice(data.value);
  }
  return data.value;
};



function isSelectable(item) {
  if ('systemAcked' in item) {
    return !item.systemAcked && !item.userAcked;
  }
  else if ('resolved' in item) {
    return !item.resolved && !item.userAcked;
  }
  else {
    return !item.userAcked;
  }
}

function isSelectAll (dataGrid) {
  let items = [];
  dataGrid.getDataSource().store().load().then((data) => {
    items = data;
  });
  let selectableItems = items.filter(isSelectable);
  let selectedRowKeys = dataGrid.option('selectedRowKeys');
  if (!selectedRowKeys || !selectedRowKeys.length) {
    return false;
  }
  return selectedRowKeys.length >= selectableItems.length ? true : undefined;
}

var noSort = {
  mode: 'none'
};
var noPaging = {
  enabled: false
};

var loadingOptions = {
  enabled: false
};

const formatNumber = {
  type: 'fixedPoint',
  precision: 0
};

const formatNotional = {
  type: 'currency',
  precision: 0
};


const addPercentToData = (data) => {
  if (undefined !== data.value) {
    return formatPercent(data.value);
  }
  return data.value;
};

const filterLiveData = (data, filterObj ={}) => {
  if (filterObj) {
    const systemAcked = filterObj?.systemAcked;
    if (null != systemAcked) {
      data = data.filter(g => g.systemAcked === systemAcked);
    }
    if (!isEmpty(filterObj.mpid)) {
      data = data.filter(g => g.mpid === filterObj.mpid);
    }
  }
  return data;

}


const isFilterNotEqual = (oldFilter = {}, newFilter = {}) => {
  if (oldFilter.mpid !== newFilter.mpid) {
    return true;
  } else if (oldFilter.systemAcked !== newFilter.systemAcked) {
    return true;
  }
  return false;

}

class RiskDataGrid extends React.Component {
  constructor(props) {
    super(props);
    this.dataGridRef = React.createRef();
    this.selectionRef =  React.createRef({
      checkBoxUpdating: false,
      selectAllCheckBox: null
    });
    this.state = {
      lastUpdateTime: null,
      data: undefined,
      isDownloadInactive: false,
      failedAttempt: SOCKET_ERROR_MAX_COUNT,
      selectedRiskDataRows: []
    };
    this.populateGridData = this.populateGridData.bind(this);
    this.getLiveGridDataAPI = this.getLiveGridDataAPI.bind(this);
    this.connectToWs = this.connectToWs.bind(this);
    this.reConnectToWs = this.reConnectToWs.bind(this);
    this.download = this.download.bind(this);
    this.renderColumns = this.renderColumns.bind(this);
    this.handleErrorResults = this.handleErrorResults.bind(this);
    this.renderSummary = this.renderSummary.bind(this);
    this.customizeTotal = this.customizeTotal.bind(this);
    this.onSelectionChanged = this.onSelectionChanged.bind(this);
    this.onEditorPreparing = this.onEditorPreparing.bind(this);
    this.sendSelectedRows = this.sendSelectedRows.bind(this);

  }

  get dataGrid() {
    var gridRef;
    if (this.dataGridRef && this.dataGridRef.current) {
      gridRef = this.dataGridRef.current.instance;
    }
    return gridRef;
  }

  componentDidUpdate(prevProps) {

    const filter = this.props.filter;
    if (filter && isFilterNotEqual(prevProps.filter, filter)) {
      this.setState(prevState => ({
        isLoaded: false,
        data: null,
      }), this.getLiveGridDataAPI);
    }
  }

  componentDidMount() {
    // try to get latest data from API
    this.getLiveGridDataAPI();
    // connect to web socket for real time data
    this.connectToWs();
  }

  componentWillUnmount() {
    this.state.wsObj && this.state.wsObj.close();
  }

  getLiveGridDataAPI() {
    let api = this.props.api;
    if (!isEmpty(api)) {
      getData(api)
        .then(data => {
          // set only if socket did not return data
          // console.log('api', data);
          if (!this.state.data) {
            this.populateGridData(data);
          }
        })
        .catch((error) => {
          const message = (error && error.response &&
            error.response.data && error.response.data) || 'Data currently unavailable';
          this.handleErrorResults(message);
        });
    }
  }

  download () {
    if (this.state.isDownloadInactive) return;
    // make it inactive for future request until current one is solved
    this.setState({ isDownloadInactive: true });
    // download  data
    let api = this.props.downloadApi;
    if (!isEmpty(api)) {
      const header = this.props.identifier && this.props.identifier.toUpperCase().replaceAll('-', '_');
      const formatDate = currentDate.format('YYYY-MM-DD')
      const name = `${header}-${formatDate}`;
      let filter = this.props.filter || {};
      filter.date = formatDate;
      const filterQuery = getQueryString(filter);
      api = `${api}?${filterQuery}`;
      downloadRiskData(api, name).then(data => {
        this.setState({ isDownloadInactive: false });
      })
        .catch((err) => {
        // error
          this.setState({ isDownloadInactive: false });
          console.log(err);
        });
    } else {
      if (this.dataGrid) {
        this.dataGrid.exportToExcel(false);
      }
      this.setState({ isDownloadInactive: false });
    }
  }

  populateGridData(data) {
    const filterObj = this.props.filter;
    const lastUpdatedField = this.props.lastUpdatedField;
    const totalField = this.props.totalField;
    let list = data.alerts;
    if (isEmptyArray(list)) {
      // console.log('No Data Found');
      list = [];
    }else{
      list = filterLiveData(list, filterObj)
    }
    const lastUpdateTime = data &&  data[lastUpdatedField] && moment(data[lastUpdatedField]).format('MM/DD/YY hh:mm:ss A');
    const totalCount = (data && data[totalField] && numberWithCommas(data[totalField])) || '';
    this.setState({
      data: list,
      lastUpdateTime: lastUpdateTime,
      totalCount: totalCount,
      isLoaded: true,
      failedAttempt: SOCKET_ERROR_MAX_COUNT
    });
  }

  handleErrorResults(errorMessage) {
    this.setState(prevState => ({
      isLoaded: true,
      data: {},
      error: errorMessage
    }));
  }

  reConnectToWs() {
    // console.log('open again');
    this.connectToWs();
  }

  connectToWs() {
    const this_ = this;
    const socketApi = this.props.ws;

    if (socketApi && this.state.failedAttempt > 0) {
      const socket = new WebSocket(this.props.ws);
      socket.addEventListener('open', function (event) {
        // console.log('connected', socketApi);
        // socket.send('Hello Server!');
      });
      socket.addEventListener('message', function (event) {
        // console.log('message from server: ' + event.data);
        let data = JSON.parse(event.data);
        this_.populateGridData(data);
      });
      socket.addEventListener('error', function (event) {
        console.log('error in', socketApi);
        console.log('error', event);
        this_.setState({
          failedAttempt: this_.state.failedAttempt - 1
        });
      });
      socket.addEventListener('close', function (event) {
        // console.log('closed', event);
        if (event && event.code !== 1005) {
          this_.reConnectToWs();
        }
      });
      this.setState({
        wsObj: socket
      });
    }
  };

  renderColumns() {
    return this.props.columns && this.props.columns.map(function (item, index) {
      if (item.type === 'number') {
        return <Column height={COLUMN_HEIGHT} format={formatNumber}  key={index + item.caption} dataField={item.dataField} caption={item.caption} />;
      } else if (item.type === 'notional') {
        return <Column height={COLUMN_HEIGHT} format={formatNotional}  key={index + item.caption} dataField={item.dataField} caption={item.caption}  so/>;
      } else if (item.type === 'percent') {
        return <Column height={COLUMN_HEIGHT} customizeText={addPercentToData} key={index + item.caption} dataField={item.dataField} caption={item.caption} />;
      } else if(item.type === 'boolean') {
        return <Column height={COLUMN_HEIGHT} dataType='string' customizeText={customizeBoolean} key={index + item.caption} dataField={item.dataField} caption={item.caption} />;
      } else if (item.type === 'right-align') {
        return <Column height={COLUMN_HEIGHT} dataType='string' key={index + item.caption} dataField={item.dataField} caption={item.caption} alignment='right' />
      } else if (item.type === 'date') {
        return <Column height={COLUMN_HEIGHT} dataType='string' customizeText={customizeDate} key={index + item.caption} dataField={item.dataField} caption={item.caption} />;
      }  else if (item.type === 'dateTime') {
        return <Column height={COLUMN_HEIGHT} dataType='string' customizeText={customizeDateTime} key={index + item.caption} dataField={item.dataField} caption={item.caption} />;
      } else if (item.type === 'time') {
        return <Column height={COLUMN_HEIGHT} dataType='string' customizeText={customizeTime} key={index + item.caption} dataField={item.dataField} caption={item.caption} />;
      }  else if (item.type === 'spPrice') {
        return <Column height={COLUMN_HEIGHT} dataType='string' customizeText={customizeSpecialPriceColumn} key={index + item.caption} dataField={item.dataField} caption={item.caption} />;
      } else {
        return <Column height={COLUMN_HEIGHT} key={index + item.caption} dataField={item.dataField} caption={item.caption} width={item.width} />;
      }
    });
  }

  renderSummary () {
    const _this = this;
    const summaryGroupTitleColumn = this.props.summaryGroupTitleColumn;
    const summaryGroupDataColumn = this.props.summaryGroupDataColumn;
    const totalField = this.props.totalField;
    if (summaryGroupTitleColumn && summaryGroupDataColumn && totalField) {
      return <Summary >
        <TotalItem
          column={summaryGroupTitleColumn.dataField}
          summaryType='count'
          alignment={'left'}
          customizeText={this.customizeSummaryTitle} />
        <TotalItem
          column={summaryGroupDataColumn.dataField}
          summaryType='count'
          alignment={'left'}
          cssClass={'dashboardGridSummaryTotalNumber'}
          customizeText={() => _this.customizeTotal} />
      </Summary>;
    } else {
      return <div />;
    }
  }

  customizeTotal (data) {
    return `${this.state.totalCount}`;
  }

  customizeSummaryTitle () {
    return `Total`;
  }

  onEditorPreparing (e)  {
    let dataGrid = e.component;
    if (e.type !== 'selection') return;
    if (e.parentType === 'dataRow' && e.row && !isSelectable(e.row.data)) { e.editorOptions.disabled = true; }
    if (e.parentType === 'headerRow') {
      e.editorOptions.onInitialized = (e) => {
        if (e.component) { this.selectionRef.current.selectAllCheckBox = e.component; }
      };
      e.editorOptions.value = isSelectAll(dataGrid);
      e.editorOptions.onValueChanged = (e) => {
        if (!e.event) {
          if (e.previousValue && this.selectionRef.current.checkBoxUpdating) { e.component.option('value', e.previousValue); }
          return;
        }
        if (isSelectAll(dataGrid) === e.value) { return; }
        e.value ? dataGrid.selectAll() : dataGrid.deselectAll();
        e.event.preventDefault();
      };
    }
  };

  onSelectionChanged(e)  {
    const deselectRowKeys = [];
    e.selectedRowsData.forEach((item) => {
      if (!isSelectable(item)) { deselectRowKeys.push(e.component.keyOf(item)); }
    });
    if (deselectRowKeys.length) {
      e.component.deselectRows(deselectRowKeys);
    }

    this.selectionRef.current.checkBoxUpdating = true;
    const selectAllCheckBox = this.selectionRef.current.selectAllCheckBox;
    selectAllCheckBox?.option('value', isSelectAll(e.component));
    this.selectionRef.current.checkBoxUpdating = false;
    this.setState({
      selectedRiskDataRows: e.component.getSelectedRowsData()
    });
  };

  sendSelectedRows() {
    this.dataGrid.deselectAll();
    this.props.onUserAckRisks(this.state.selectedRiskDataRows)
  }


  render() {
    const header = this.props.header;
    const downloadHeader = this.props.identifier && this.props.identifier.toUpperCase().replaceAll('-', '_');
    const downloadFilename = `${downloadHeader}-${currentDate.format('MM/DD/YYYY')}`;
    const data = this.state.data;
    const onUserAckRisks = this.props.onUserAckRisks;
    const renderAckRisk = onUserAckRisks && !isEmptyArray(this.state.selectedRiskDataRows);

    return (
      <div className={classNames(styles.container)}>
        <TableHeader headerUpperCase={false}
          header={header}
          className={styles.tableHeader}
          download={this.download}
          downloadIconId={this.props.identifier.concat('-download')}
          textLink={renderAckRisk ? RISK_ACK_TEXT_LINK : null}
          onTextLinkClick={renderAckRisk ? this.sendSelectedRows : null}
          textLinkClassName={renderAckRisk ? styles.textLinkClassName : null}
          tableHeaderMargin={false}
          topBorderOnly={false}
          bottomBorderOnly />
        <Loading
          type='table'
          data={data}
          loaded={this.state.isLoaded}
          reloading={false}
          error={this.state.error}
          emptyMessage='No results found.' >
          <DataGrid
            ref={this.dataGridRef}
            id={this.props.identifier}
            dataSource={this.state.data}
            columnAutoWidth
            showColumnLines
            showRowLines
            rowAlternationEnabled
            showBorders
            repaintChangesOnly
            paging={noPaging}
            sorting={noSort}
            loadPanel={loadingOptions}
            height={this.props.maxRows ? (this.props.maxRows + 1) * COLUMN_HEIGHT : 450}
            cacheEnabled={false}
            onEditorPreparing={onUserAckRisks && this.onEditorPreparing}
            onSelectionChanged={onUserAckRisks && this.onSelectionChanged}
            remoteOperations={false}
          >
            {this.renderColumns()}

            <Scrolling mode='virtual' />
            <Export enabled fileName={downloadFilename} />
            {this.renderSummary()}
            <ColumnFixing enabled />
            
          {onUserAckRisks &&  <Selection
             ref={this.selectionRef}
            mode='multiple'
            showCheckBoxesMode={'always'}
          /> }

          </DataGrid>
        
        </Loading>
        <div id='lastUpdated' className={styles.textNote} >Update: <span className={styles.lastUpdated}>{this.state.lastUpdateTime}</span></div>
      </div>

    );
  }
}

RiskDataGrid.propTypes = {
  ws: PropTypes.string.isRequired,
  api: PropTypes.string.isRequired,
  downloadApi: PropTypes.string,
  header: PropTypes.string.isRequired,
  columns: PropTypes.array.isRequired,
  lastUpdatedField: PropTypes.string.isRequired,
  totalField: PropTypes.string,
  identifier: PropTypes.string.isRequired,
  onUserAckRisks: PropTypes.func,
  maxRows: PropTypes.number
};

export default RiskDataGrid;
